
import React, { useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AppContext } from '../App';
import { supabase } from '../supabase';
import { Client, BriefingField } from '../types';

const Clients: React.FC = () => {
  const context = useContext(AppContext);
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'briefing'>('info');
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [logoUrlPreview, setLogoUrlPreview] = useState('');
  const [docType, setDocType] = useState<'CNPJ' | 'CPF'>('CNPJ');

  const formatDoc = (value: string, type: 'CNPJ' | 'CPF') => {
    const digits = value.replace(/\D/g, '');
    if (type === 'CPF') {
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2')
        .substring(0, 14);
    }
    return digits
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .substring(0, 18);
  };

  useEffect(() => {
    if (location.state && (location.state as any).openModal) {
      setIsModalOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    if (editingClient) {
      setLogoUrlPreview(editingClient.logoUrl);
    } else {
      setLogoUrlPreview('');
    }
  }, [editingClient]);

  if (!context) return null;
  const { clients, setClients, searchTerm } = context;

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSaveClient = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const clientData = {
      name: formData.get('name') as string,
      trading_name: formData.get('tradingName') as string,
      type: formData.get('type') as string,
      contact_name: formData.get('contactName') as string,
      cnpj: formData.get('cnpj') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      address: formData.get('address') as string,
      website: formData.get('website') as string,
      logo_url: formData.get('logoUrl') as string,
      avatar_url: `https://ui-avatars.com/api/?name=${formData.get('contactName')}&background=fcebf5&color=8a0049&rounded=true`
    };

    if (editingClient) {
      const { error } = await supabase
        .from('clients')
        .update(clientData)
        .eq('id', editingClient.id);
      if (error) alert(error.message);
    } else {
      const newId = `#CL-${Math.floor(Math.random() * 9000) + 1000}`;
      const { error } = await supabase
        .from('clients')
        .insert([{ id: newId, ...clientData, status: 'Active', active_projects_count: 0, briefing: [] }]);
      if (error) alert(error.message);
    }
    closeModal();
  };

  const deleteClient = async (id: string) => {
    if (confirm('Excluir este cliente?')) {
      const { error } = await supabase.from('clients').delete().eq('id', id);
      if (error) alert(error.message);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
    setActiveTab('info');
  };

  const openEdit = (client: Client, tab: 'info' | 'briefing' = 'info') => {
    setEditingClient(client);
    setActiveTab(tab);
    setIsModalOpen(true);
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto flex flex-col gap-6 md:gap-8 animate-in fade-in duration-500">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <h2 className="text-3xl md:text-4xl font-black text-text-main tracking-tight">Clientes</h2>
          <p className="text-text-secondary font-bold text-base md:text-lg mt-1">Base unificada de parceiros e briefings.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
          <button
            onClick={() => {
              const url = `${window.location.origin}${window.location.pathname}#/onboarding`;
              navigator.clipboard.writeText(url);
              alert('Link copiado!');
            }}
            className="flex items-center justify-center gap-2 h-14 px-8 rounded-2xl border-2 border-primary/20 text-primary text-[10px] font-black uppercase tracking-widest hover:bg-primary/5 transition-all w-full sm:w-auto"
          >
            <span className="material-symbols-outlined text-[20px]">link</span>
            Link de Onboarding
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center justify-center gap-2 h-14 px-8 rounded-2xl bg-primary text-white text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all w-full sm:w-auto"
          >
            <span className="material-symbols-outlined text-[20px]">add</span>
            Novo Cliente
          </button>
        </div>
      </div>

      <div className="hidden md:block bg-white rounded-[23px] border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50/50 border-b border-gray-100">
              <th className="px-10 py-6 text-[10px] font-black text-text-secondary uppercase tracking-widest">Empresa</th>
              <th className="px-10 py-6 text-[10px] font-black text-text-secondary uppercase tracking-widest">CNPJ / Tipo</th>
              <th className="px-10 py-6 text-[10px] font-black text-text-secondary uppercase tracking-widest">Contato</th>
              <th className="px-10 py-6 text-[10px] font-black text-text-secondary uppercase tracking-widest text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredClients.map((client) => (
              <tr key={client.id} className="group hover:bg-primary/[0.01] transition-all">
                <td className="px-10 py-6">
                  <div className="flex items-center gap-5">
                    <img src={client.logoUrl} className="size-14 rounded-2xl shadow-sm border border-gray-100 object-cover bg-white shrink-0" alt="" />
                    <div className="flex flex-col">
                      <span className="text-base font-black text-text-main group-hover:text-primary transition-colors">{client.name}</span>
                      <span className="text-[11px] font-bold text-text-secondary uppercase tracking-widest">{client.id}</span>
                    </div>
                  </div>
                </td>
                <td className="px-10 py-6">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-bold text-text-main">{client.cnpj}</span>
                    <span className="w-fit px-2 py-0.5 rounded-md text-[9px] font-black bg-primary-light text-primary uppercase tracking-wider">{client.type}</span>
                  </div>
                </td>
                <td className="px-10 py-6">
                  <div className="flex items-center gap-3">
                    <img src={client.avatarUrl} className="size-9 rounded-full border-2 border-white shadow-sm" alt="" />
                    <div className="flex flex-col">
                      <span className="text-sm font-black text-text-main">{client.contactName}</span>
                      <span className="text-[10px] font-bold text-text-secondary">{client.email}</span>
                    </div>
                  </div>
                </td>
                <td className="px-10 py-6 text-right">
                  <div className="flex items-center justify-end gap-3">
                    <button onClick={() => openEdit(client, 'briefing')} className="size-10 flex items-center justify-center rounded-xl text-gray-300 hover:bg-gray-100 hover:text-primary transition-all" title="Ver Briefing">
                      <span className="material-symbols-outlined">description</span>
                    </button>
                    <button onClick={() => openEdit(client, 'info')} className="size-10 flex items-center justify-center rounded-xl text-gray-300 hover:bg-gray-100 hover:text-primary transition-all">
                      <span className="material-symbols-outlined">edit</span>
                    </button>
                    <button onClick={() => deleteClient(client.id)} className="size-10 flex items-center justify-center rounded-xl text-gray-300 hover:bg-red-50 hover:text-red-600 transition-all">
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-text-main/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-3xl rounded-[23px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[95vh] sm:max-h-[90vh]">
            <div className="p-8 md:p-10 border-b border-gray-50 flex flex-col gap-6 shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-xl md:text-2xl font-black text-text-main tracking-tight">
                  {editingClient ? editingClient.name : 'Novo Cliente'}
                </h3>
                <button onClick={closeModal} className="size-12 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all text-text-secondary">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              {editingClient && (
                <div className="flex gap-2 p-1 bg-gray-100/60 rounded-2xl w-fit self-center">
                  <button
                    onClick={() => setActiveTab('info')}
                    className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'info' ? 'bg-white shadow-sm text-primary' : 'text-text-secondary'}`}
                  >
                    Informações
                  </button>
                  <button
                    onClick={() => setActiveTab('briefing')}
                    className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'briefing' ? 'bg-white shadow-sm text-primary' : 'text-text-secondary'}`}
                  >
                    Briefing
                  </button>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-6 md:p-10">
              {activeTab === 'info' ? (
                <form onSubmit={handleSaveClient} className="flex flex-col gap-6 md:gap-8">
                  <div className="flex flex-col sm:flex-row items-center gap-6 p-6 bg-primary-light/30 rounded-[23px] border border-primary/5">
                    <div className="size-24 rounded-3xl bg-white border-2 border-dashed border-primary/20 flex items-center justify-center overflow-hidden shrink-0 shadow-inner">
                      {logoUrlPreview ? (
                        <img src={logoUrlPreview} className="w-full h-full object-cover" onError={() => setLogoUrlPreview('')} alt="Preview" />
                      ) : (
                        <span className="material-symbols-outlined text-primary/30 text-3xl">image</span>
                      )}
                    </div>
                    <div className="flex-1 flex flex-col gap-2 w-full">
                      <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary ml-1">Logo URL</label>
                      <input name="logoUrl" defaultValue={editingClient?.logoUrl} onChange={(e) => setLogoUrlPreview(e.target.value)} className="h-12 px-5 rounded-2xl bg-white border border-gray-200 outline-none text-sm font-bold shadow-sm focus:border-primary/20 transition-all" placeholder="https://..." />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Nome Fantasia</label>
                      <input name="name" defaultValue={editingClient?.name} required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Razão Social</label>
                      <input name="tradingName" defaultValue={editingClient?.tradingName} className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex justify-between items-center ml-1">
                        <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest">{docType}</label>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setDocType('CNPJ')} className={`text-[8px] font-black px-2 py-1 rounded-md transition-all ${docType === 'CNPJ' ? 'bg-primary text-white' : 'bg-gray-100 text-text-secondary'}`}>CNPJ</button>
                          <button type="button" onClick={() => setDocType('CPF')} className={`text-[8px] font-black px-2 py-1 rounded-md transition-all ${docType === 'CPF' ? 'bg-primary text-white' : 'bg-gray-100 text-text-secondary'}`}>CPF</button>
                        </div>
                      </div>
                      <input 
                        name="cnpj" 
                        defaultValue={editingClient?.cnpj} 
                        onChange={(e) => e.target.value = formatDoc(e.target.value, docType)}
                        className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" 
                        placeholder={docType === 'CNPJ' ? "00.000.000/0001-00" : "000.000.000-00"}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Tipo de Cliente</label>
                      <select name="type" defaultValue={editingClient?.type || 'SaaS'} className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all">
                        <option value="SaaS">SaaS</option>
                        <option value="Serviços">Serviços</option>
                        <option value="Varejo">Varejo</option>
                        <option value="Outro">Outro</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Nome do Contato</label>
                      <input name="contactName" defaultValue={editingClient?.contactName} className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">E-mail</label>
                      <input name="email" type="email" defaultValue={editingClient?.email} className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Telefone / WhatsApp</label>
                      <input name="phone" defaultValue={editingClient?.phone} className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Site / Portfólio</label>
                      <input name="website" defaultValue={editingClient?.website} className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Endereço Completo</label>
                    <input name="address" defaultValue={editingClient?.address} className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all" />
                  </div>

                  <button type="submit" className="h-16 rounded-2xl bg-primary text-white font-black uppercase tracking-widest shadow-2xl shadow-primary/40 hover:scale-[1.01] transition-all">
                    Salvar Cadastro
                  </button>
                </form>
              ) : (
                <div className="flex flex-col gap-6 animate-in fade-in duration-300">
                  {editingClient?.briefing && editingClient.briefing.length > 0 ? (
                    editingClient.briefing.map((field) => (
                      <div key={field.id} className="p-6 rounded-[23px] bg-gray-50/50 border border-gray-100">
                        <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em] mb-2">{field.label}</p>
                        <p className="text-sm font-bold text-text-main leading-relaxed">
                          {field.value || <span className="text-text-secondary opacity-40 italic">Não respondido</span>}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="py-20 flex flex-col items-center text-center">
                      <span className="material-symbols-outlined text-5xl text-gray-200 mb-4">edit_note</span>
                      <p className="text-sm font-bold text-text-secondary">Nenhuma resposta de briefing encontrada para este cliente.</p>
                      <p className="text-[10px] uppercase font-black text-gray-300 mt-2 tracking-widest">O onboarding ainda não foi preenchido.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
